import React, { lazy, Suspense, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { track } from "./utils/analytics";
import { HelmetProvider } from "react-helmet-async";
import ErrorBoundary from "./components/ErrorBoundary";
import { AuthProvider } from "./contexts/AuthContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { Toaster } from "./components/ui/sonner";
import ProtectedRoute from "./components/ProtectedRoute";
import AuthCallback from "./components/AuthCallback";
import NeedHelpWidget from "./components/NeedHelpWidget";
import MobileBottomNav from "./components/MobileBottomNav";

// Lazy-loaded pages — split into per-route chunks
const Landing                      = lazy(() => import("./pages/LandingRedesignV2"));
const EmployerLanding              = lazy(() => import("./pages/EmployerLanding"));
const Login                        = lazy(() => import("./pages/Login"));
const Signup                       = lazy(() => import("./pages/Signup"));
const VerifyEmail                  = lazy(() => import("./pages/VerifyEmail"));
const VerifyEmailPending           = lazy(() => import("./pages/VerifyEmailPending"));
const ForgotPassword               = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword                = lazy(() => import("./pages/ResetPassword"));
const GoogleAuthCallback           = lazy(() => import("./pages/GoogleAuthCallback"));
const RoleSelection                = lazy(() => import("./pages/RoleSelection"));
const StudentDashboard             = lazy(() => import("./pages/StudentDashboard"));
const EmployerDashboard            = lazy(() => import("./pages/EmployerDashboard"));
const JobsPage                     = lazy(() => import("./pages/JobsPage"));
const JobDetailPage                = lazy(() => import("./pages/JobDetailPage"));
const JobLocationPage              = lazy(() => import("./pages/JobLocationPage"));
const JobRolePage                  = lazy(() => import("./pages/JobRolePage"));
const FeedPage                     = lazy(() => import("./pages/FeedPage"));
const ContactPage                  = lazy(() => import("./pages/ContactPage"));
const StudentPricingPage           = lazy(() => import("./pages/StudentPricingPage"));
const ForSchoolsPage               = lazy(() => import("./pages/ForSchoolsPage"));
const ForUniversitiesPage          = lazy(() => import("./pages/ForUniversitiesPage"));
const CompanyPage                  = lazy(() => import("./pages/CompanyPage"));
const SalaryGuidePage              = lazy(() => import("./pages/SalaryGuidePage"));
const IndustryNewsPage             = lazy(() => import("./pages/IndustryNewsPage"));
const PortfolioGallery             = lazy(() => import("./pages/PortfolioGallery"));
const SkillAssessments             = lazy(() => import("./pages/SkillAssessments"));
const RecommendationSubmit         = lazy(() => import("./pages/RecommendationSubmit"));
const ExpertsPage                  = lazy(() => import("./pages/ExpertsPage"));
const ExpertDetailPage             = lazy(() => import("./pages/ExpertDetailPage"));
const SessionsPage                 = lazy(() => import("./pages/SessionsPage"));
const ProfileScorePage             = lazy(() => import("./pages/ProfileScorePage"));
const InternationalPage            = lazy(() => import("./pages/InternationalPage"));
const InternationalDetailPage      = lazy(() => import("./pages/InternationalDetailPage"));
const InternationalApplicationsPage = lazy(() => import("./pages/InternationalApplicationsPage"));
const PrivacyPolicy                = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService               = lazy(() => import("./pages/TermsOfService"));
const RefundPolicy                 = lazy(() => import("./pages/RefundPolicy"));
const CareerPathsPage              = lazy(() => import("./pages/CareerPathsPage"));
const RMDashboard                  = lazy(() => import("./pages/RMDashboard"));
const CounsellorDashboard          = lazy(() => import("./pages/CounsellorDashboard"));
const CounsellorLogin              = lazy(() => import("./pages/CounsellorLogin"));
const InternshipsPage              = lazy(() => import("./pages/InternshipsPage"));
const InternshipDetailPage         = lazy(() => import("./pages/InternshipDetailPage"));
const MyInternshipApplications     = lazy(() => import("./pages/MyInternshipApplications"));
const AddPhone                     = lazy(() => import("./pages/AddPhone"));
const FoundingMemberPage           = lazy(() => import("./pages/FoundingMemberPage"));
const PhoneSetup                   = lazy(() => import("./pages/PhoneSetup"));
const AccountSettings              = lazy(() => import("./pages/AccountSettings"));
const NotificationSettings         = lazy(() => import("./pages/NotificationSettings"));
const PostJob                      = lazy(() => import("./pages/PostJob"));
const HiringCalendar               = lazy(() => import("./pages/HiringCalendar"));
const GigsPage                     = lazy(() => import("./pages/GigsPage"));
const PostGig                      = lazy(() => import("./pages/PostGig"));
const PricingPage                  = lazy(() => import("./pages/PricingPage"));
const CertificatesPage             = lazy(() => import("./pages/CertificatesPage"));
const PaymentSuccess               = lazy(() => import("./pages/PaymentSuccess"));
const InterviewQuestionBank        = lazy(() => import("./pages/InterviewQuestionBank"));
const CareerPathPage               = lazy(() => import("./pages/CareerPathPage"));
const NotFound                     = lazy(() => import("./pages/NotFound"));
const CertificateVerify            = lazy(() => import("./pages/CertificateVerify"));

// AI Feature Pages
const AIResumeBuilder  = lazy(() => import("./pages/AIResumeBuilder"));
const AIMockInterview  = lazy(() => import("./pages/AIMockInterview"));
const AIMentorship     = lazy(() => import("./pages/AIMentorship"));
const AICourses        = lazy(() => import("./pages/AICourses"));
const AICoverLetter    = lazy(() => import("./pages/AICoverLetter"));
const AIPortfolio      = lazy(() => import("./pages/AIPortfolio"));
const AISkillGap       = lazy(() => import("./pages/AISkillGap"));

// Admin Pages
const AdminLogin                = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard            = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsers                = lazy(() => import("./pages/admin/AdminUsers"));
const AdminEmployers            = lazy(() => import("./pages/admin/AdminEmployers"));
const AdminJobs                 = lazy(() => import("./pages/admin/AdminJobs"));
const AdminSubscriptions        = lazy(() => import("./pages/admin/AdminSubscriptions"));
const AdminStudentPricing       = lazy(() => import("./pages/admin/AdminStudentPricing"));
const AdminSchoolLeads          = lazy(() => import("./pages/admin/AdminSchoolLeads"));
const AdminUniversityLeads      = lazy(() => import("./pages/admin/AdminUniversityLeads"));
const AdminHiringExpertBookings = lazy(() => import("./pages/admin/AdminHiringExpertBookings"));
const AdminSupport              = lazy(() => import("./pages/admin/AdminSupport"));
const AdminAnalytics            = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminTeam                 = lazy(() => import("./pages/admin/AdminTeam"));
const AdminSettings             = lazy(() => import("./pages/admin/AdminSettings"));
const AdminAdvancedSettings     = lazy(() => import("./pages/admin/AdminAdvancedSettings"));
const AdminAuditLogs            = lazy(() => import("./pages/admin/AdminAuditLogs"));
const AdminInternational        = lazy(() => import("./pages/admin/AdminInternational"));
const AdminSuperDashboard       = lazy(() => import("./pages/admin/AdminSuperDashboard"));

const PageLoader = () => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
        <div style={{ width: 40, height: 40, border: "3px solid #4F46E5", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
);

const AppRouter = () => {
    const location = useLocation();

    // SPA pageviews — PostHog only auto-captures the initial load
    useEffect(() => {
        track('$pageview', { path: location.pathname });
    }, [location.pathname]);

    if (location.hash?.includes('session_id=')) {
        return <AuthCallback />;
    }

    return (
        <Suspense fallback={<PageLoader />}>
            <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Landing />} />
                <Route path="/employers" element={<EmployerLanding />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/verify-email-pending" element={<VerifyEmailPending />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/auth/callback" element={<GoogleAuthCallback />} />
                <Route path="/jobs" element={<JobsPage />} />
                <Route path="/jobs/in/:city" element={<JobLocationPage />} />
                <Route path="/jobs/for/:role" element={<JobRolePage />} />
                <Route path="/jobs/:jobId" element={<JobDetailPage />} />
                <Route path="/gigs" element={<GigsPage />} />
                <Route path="/companies/:employerId" element={<CompanyPage />} />
                <Route path="/salary-guide" element={<SalaryGuidePage />} />
                <Route path="/industry-news" element={<IndustryNewsPage />} />
                <Route path="/portfolio" element={<PortfolioGallery />} />
                <Route path="/skill-assessments" element={<SkillAssessments />} />
                <Route path="/recommendations/submit/:token" element={<RecommendationSubmit />} />
                <Route path="/pricing" element={<StudentPricingPage />} />
                <Route path="/student-pricing" element={<StudentPricingPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/founding" element={<FoundingMemberPage />} />
                <Route path="/for-schools" element={<ForSchoolsPage />} />
                <Route path="/for-universities" element={<ForUniversitiesPage />} />
                <Route path="/experts" element={<ExpertsPage />} />
                <Route path="/experts/:expertId" element={<ExpertDetailPage />} />
                <Route path="/international" element={<InternationalPage />} />
                <Route path="/international/:opportunityId" element={<InternationalDetailPage />} />
                <Route path="/interview-questions" element={<InterviewQuestionBank />} />
                <Route path="/career-paths" element={<CareerPathsPage />} />
                <Route path="/career-paths/:pathId" element={<CareerPathPage />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/refund-policy" element={<RefundPolicy />} />
                {/* Consolidated: one resume builder entry point (AI) — old print-template builder redirected */}
                <Route path="/resume-builder" element={<Navigate to="/ai/resume-builder" replace />} />
                <Route path="/internships" element={<InternshipsPage />} />
                <Route path="/internships/:internshipId" element={<InternshipDetailPage />} />
                <Route path="/certificates" element={<CertificatesPage />} />
                <Route path="/certificates/:certificateId" element={<CertificatesPage />} />
                <Route path="/verify/:certificateId" element={<CertificateVerify />} />

                {/* Phone setup */}
                <Route path="/phone-setup" element={<ProtectedRoute><PhoneSetup /></ProtectedRoute>} />
                <Route path="/add-phone" element={<ProtectedRoute><AddPhone /></ProtectedRoute>} />

                {/* Dashboard / role selection */}
                <Route path="/dashboard" element={<ProtectedRoute><RoleSelection /></ProtectedRoute>} />
                <Route path="/role-selection" element={<ProtectedRoute><RoleSelection /></ProtectedRoute>} />

                {/* Feed */}
                <Route path="/feed" element={<ProtectedRoute requiredRole="STUDENT"><FeedPage /></ProtectedRoute>} />
                <Route path="/feed/:postId" element={<ProtectedRoute requiredRole="STUDENT"><FeedPage /></ProtectedRoute>} />

                {/* Premium / pricing */}
                <Route path="/premium" element={<StudentPricingPage />} />
                <Route path="/pricing-plans" element={<PricingPage />} />

                {/* AI Feature Routes */}
                <Route path="/ai/resume-builder" element={<ProtectedRoute><AIResumeBuilder /></ProtectedRoute>} />
                <Route path="/ai/mock-interview" element={<ProtectedRoute><AIMockInterview /></ProtectedRoute>} />
                <Route path="/ai/mentorship" element={<ProtectedRoute><AIMentorship /></ProtectedRoute>} />
                <Route path="/ai/courses" element={<ProtectedRoute><AICourses /></ProtectedRoute>} />
                <Route path="/ai/cover-letter" element={<ProtectedRoute><AICoverLetter /></ProtectedRoute>} />
                <Route path="/ai/portfolio" element={<ProtectedRoute><AIPortfolio /></ProtectedRoute>} />
                <Route path="/ai/skill-gap" element={<ProtectedRoute><AISkillGap /></ProtectedRoute>} />
                <Route path="/ai/profile-score" element={<ProtectedRoute requiredRole="STUDENT"><ProfileScorePage /></ProtectedRoute>} />

                {/* Payment */}
                <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />

                {/* Student Routes */}
                <Route path="/student/dashboard" element={<ProtectedRoute requiredRole="STUDENT"><StudentDashboard /></ProtectedRoute>} />
                <Route path="/student/dashboard/*" element={<ProtectedRoute requiredRole="STUDENT"><StudentDashboard /></ProtectedRoute>} />
                <Route path="/student/sessions" element={<ProtectedRoute requiredRole="STUDENT"><SessionsPage /></ProtectedRoute>} />
                <Route path="/student/international-applications" element={<ProtectedRoute requiredRole="STUDENT"><InternationalApplicationsPage /></ProtectedRoute>} />
                <Route path="/student/internship-applications" element={<ProtectedRoute requiredRole="STUDENT"><MyInternshipApplications /></ProtectedRoute>} />

                {/* Employer Routes */}
                <Route path="/employer/dashboard" element={<ProtectedRoute requiredRole="EMPLOYER"><EmployerDashboard /></ProtectedRoute>} />
                <Route path="/employer/dashboard/*" element={<ProtectedRoute requiredRole="EMPLOYER"><EmployerDashboard /></ProtectedRoute>} />
                <Route path="/employer/post-job" element={<ProtectedRoute requiredRole="EMPLOYER"><PostJob /></ProtectedRoute>} />
                <Route path="/employer/hiring-calendar" element={<ProtectedRoute requiredRole="EMPLOYER"><HiringCalendar /></ProtectedRoute>} />
                <Route path="/employer/post-gig" element={<ProtectedRoute requiredRole="EMPLOYER"><PostGig /></ProtectedRoute>} />

                {/* Settings */}
                <Route path="/settings/notifications" element={<ProtectedRoute><NotificationSettings /></ProtectedRoute>} />
                <Route path="/settings/account" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />

                {/* Catch-all */}
                <Route path="*" element={<NotFound />} />
            </Routes>
        </Suspense>
    );
};

const AdminRouter = () => (
    <Suspense fallback={<PageLoader />}>
        <Routes>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/employers" element={<AdminEmployers />} />
            <Route path="/admin/jobs" element={<AdminJobs />} />
            <Route path="/admin/subscriptions" element={<AdminSubscriptions />} />
            <Route path="/admin/student-pricing" element={<AdminStudentPricing />} />
            <Route path="/admin/school-leads" element={<AdminSchoolLeads />} />
            <Route path="/admin/university-leads" element={<AdminUniversityLeads />} />
            <Route path="/admin/hiring-expert-bookings" element={<AdminHiringExpertBookings />} />
            <Route path="/admin/support" element={<AdminSupport />} />
            <Route path="/admin/analytics" element={<AdminAnalytics />} />
            <Route path="/admin/team" element={<AdminTeam />} />
            <Route path="/admin/settings" element={<AdminSettings />} />
            <Route path="/admin/advanced-settings" element={<AdminAdvancedSettings />} />
            <Route path="/admin/controls" element={<AdminAdvancedSettings />} />
            <Route path="/admin/audit" element={<AdminAuditLogs />} />
            <Route path="/admin/international" element={<AdminInternational />} />
            <Route path="/admin/super" element={<AdminSuperDashboard />} />
            <Route path="/admin/super/*" element={<AdminSuperDashboard />} />
            <Route path="/admin/rm" element={<RMDashboard />} />
            <Route path="/admin/counsellor" element={<CounsellorDashboard />} />
            <Route path="/counsellor/login" element={<CounsellorLogin />} />
            <Route path="/counsellor" element={<CounsellorLogin />} />
            <Route path="/admin/*" element={<AdminDashboard />} />
        </Routes>
    </Suspense>
);

const CombinedRouter = () => {
    const location = useLocation();
    if (location.pathname.startsWith('/admin')) {
        return <AdminRouter />;
    }
    return <AppRouter />;
};

function App() {
    return (
        <HelmetProvider>
            <div className="App">
                <BrowserRouter>
                    <AuthProvider>
                        <NotificationProvider>
                            <ErrorBoundary>
                                <CombinedRouter />
                                <MobileBottomNav />
                                <NeedHelpWidget />
                                <Toaster
                                    position="top-right"
                                    toastOptions={{
                                        style: {
                                            background: '#0F172A',
                                            border: '1px solid rgba(255,255,255,0.1)',
                                            color: '#F8FAFC'
                                        }
                                    }}
                                />
                            </ErrorBoundary>
                        </NotificationProvider>
                    </AuthProvider>
                </BrowserRouter>
            </div>
        </HelmetProvider>
    );
}

export default App;
